import Observable from 'zen-observable';
import { ApolloClient } from 'apollo-client';
import { HttpLink } from 'apollo-link-http';
import { setContext } from 'apollo-link-context';
import {
  InMemoryCache,
  defaultDataIdFromObject,
  IntrospectionFragmentMatcher
} from 'apollo-cache-inmemory';
import { getToken, isAuthenticated, clearSessionStorage } from 'utils/auth';
import { onError } from 'apollo-link-error';
import { ERROR_INVALID_SESSION } from 'utils/messages';
import { safelyCloseWebSocketLink } from 'utils/functions';
import { WebSocketLink } from 'apollo-link-ws';
import { split, ApolloLink } from 'apollo-link';
import { DedupLink } from 'apollo-link-dedup';
import { getMainDefinition } from 'apollo-utilities';
import introspectionQueryResultData from './fragmentTypes.json';
import { WINDCREEK_LEGACY_URL } from 'utils/constants';

export default function configureApollo(history) {
  const httpLink = new HttpLink({
    uri: process.env.REACT_APP_GRAPHQL_API_URI,
    credentials: 'same-origin' // For maintenance cookie
  });

  // Create a WebSocket link.
  // wsLink is shared by swappableWsLink and recreateWebSocketConnection
  let wsLink;
  const swappableWsLink = new ApolloLink(operation => {
    return wsLink
      ? wsLink.request(operation) || Observable.of()
      : Observable.of();
  });

  const recreateWebSocketConnection = function() {
    safelyCloseWebSocketLink(wsLink);

    const userToken = getToken();
    wsLink = userToken
      ? new WebSocketLink({
          uri: process.env.REACT_APP_GRAPHQLWS_API_URI + '/' + userToken,
          options: {
            reconnect: true
          }
        })
      : null;
  };
  recreateWebSocketConnection();

  const authLink = setContext((_, { headers }) => {
    // get the authentication token from local storage if it exists
    const token = getToken();
    const newHeaders = token
      ? { ...headers, authorization: `Bearer ${token}` }
      : headers;

    // return the headers to the context so httpLink can read them
    return {
      headers: newHeaders
    };
  });

  const logoutLink = onError(({ networkError }) => {
    if (networkError && networkError.statusCode === 401 && isAuthenticated()) {
      clearSessionStorage();
      recreateWebSocketConnection();
      history.push('/login', {
        error: {
          statusCode: networkError.statusCode,
          message: ERROR_INVALID_SESSION
        }
      });
    } else if (networkError && networkError.statusCode === 423) {
      // once user is logged in, specific calls may be locked.
      // Currently the one case this occurs is if a new TOS needs to be
      // agreed to.  In this case, we send them to legacy, which handles display
      // and acceptance, and then sends them back to newux.
      window.location.replace(WINDCREEK_LEGACY_URL);
    }
  });

  const dedupLink = new DedupLink();

  const combinedHttpLink = logoutLink.concat(
    authLink.concat(dedupLink.concat(httpLink))
  );

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  const link = split(
    // split based on operation type
    ({ query }) => {
      const { kind, operation } = getMainDefinition(query);
      return kind === 'OperationDefinition' && operation === 'subscription';
    },
    swappableWsLink,
    combinedHttpLink
  );

  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData
  });

  const cache = new InMemoryCache({
    fragmentMatcher,
    dataIdFromObject: object => {
      switch (object.__typename) {
        case 'User':
          return `User:${object.userId}`;
        case 'Posts':
          return `Posts`;
        case 'CommunityMessages':
          return 'CommunityMessages';
        case 'MaintenanceMode':
          return 'MaintenanceMode';
        default:
          return defaultDataIdFromObject(object); // fall back to default handling
      }
    }
  });

  const client = new ApolloClient({
    link,
    cache: cache,
    connectToDevTools: true // Disable this for Prod! See https://github.com/apollographql/apollo-client-devtools
  });

  client.recreateWebSocketConnection = recreateWebSocketConnection;

  return client;
}



// WEBPACK FOOTER //
// ./src/configureApollo.js